import type {
    IStekzFieldProperties,
    IStekzSchema,
} from "@/wc/registry/lib/service/repository/types";
import type IFormInputTypeComponent from "@/wc/registry/types/form-input-type";
import type {
    IRepositoryParameters,
    IFields,
} from "@/wc/registry/lib/service/repository/types";

import serviceRepository from "@/wc/registry/lib/service/repository/index.js";
import * as dom from "@/wc/registry/lib/dom/index.js";
import { getFieldProperties } from "@/wc/registry/lib/schema/index.js";
import { get as lodashGet, set as lodashSet } from "lodash";
import { monitoring } from "@/lib/integrations/index.js";

// @TODO: Add support for 'bank', 'country'.
const supportedFormats = [
    "phone",
    "email",
    "textarea",
    "presigned-fileupload",
    "date",
    "date-time",
    "time",
];
// To silence a warning about an unsupported format, add the format here.
const ignoreFormats = ["bank", "country"];

// There are certain stekz properties which we don't directly want to convert
// to form input types. To map to form input types we sometimes convert
// between certain combinations of 'format' and 'type' to 'formInputType'.
export function convertPropertyToFormInputType(
    property: IStekzFieldProperties
): string {
    let formInputType = undefined;

    if (property.format && supportedFormats.includes(property.format)) {
        formInputType = property.format;
    } else {
        if (property.format && !ignoreFormats.includes(property.format)) {
            console.warn(
                `Unsupported format: '${property.format}', falling back to '${property.type}'.`
            );
        }
        formInputType = property.type;
    }

    // When type is integer or string and a service-repository is listed in
    // the property, the form-input that is to be rendered should
    // actually be a choice-field.
    if (
        ["integer", "string"].includes(formInputType) &&
        "service-repository" in property
    ) {
        formInputType = "choice";
    }

    // Sometimes enums are present in properties, which means only these
    // enums can be picked as options for the input. Turn the input type
    // of the form into 'choice' and configure the options mentioned
    // in the enum.
    if (formInputType === "string" && "enum" in property) {
        formInputType = "choice";
    }

    return formInputType;
}

// Enrich a form input with information provided by the IStekzFieldProperties.
// Could also be used to populate a fake formInput, to e.g. do some diffing
// to partially autogenerate form inputs.
export function enrichFormInput(
    formInput:
        | HTMLElementTagNameMap["c-form-input"]
        | { dataset: { [name: string]: string } },
    key: string,
    fieldProperties: IStekzFieldProperties
):
    | HTMLElementTagNameMap["c-form-input"]
    | { dataset: { [name: string]: string } } {
    if ("service-repository" in fieldProperties) {
        formInput.dataset.serviceRepository =
            fieldProperties["service-repository"];
    }

    if ("preview-mode" in fieldProperties) {
        formInput.dataset.previewMode = fieldProperties["preview-mode"];
    }

    if ("show-url" in fieldProperties) {
        formInput.dataset.showUrl = fieldProperties["show-url"];
    }

    if ("key" in fieldProperties) {
        formInput.dataset.key = fieldProperties["key"];
    }

    if ("key-description" in fieldProperties) {
        formInput.dataset.keyDescription = fieldProperties["key-description"];
    }

    // Append the language code (e.g. 'EN') to the form-input, to provide some
    // visual indication about what translation is being edited.
    if (
        "x-translation" in fieldProperties &&
        fieldProperties["x-translation"] === true
    ) {
        // Convert e.g. 'value.en' to 'EN'.
        const parts = key.split(".");
        const languageCode = parts[parts.length - 1].toUpperCase();
        formInput.dataset.appendValue = languageCode;
    }

    const formInputType = convertPropertyToFormInputType(fieldProperties);
    // Sometimes enums are present in fieldProperties, which means only these
    // enums can be picked as options for the input. Turn the input type
    // of the form into 'choice' and configure the options mentioned
    // in the enum.
    if (formInputType === "choice" && "enum" in fieldProperties) {
        const choices = fieldProperties.enum.map((value: string) => ({
            value,
            name: value,
        }));
        formInput.dataset.choices = JSON.stringify(choices);
    }

    if ("choices" in fieldProperties) {
        formInput.dataset.choices = JSON.stringify(fieldProperties.choices);
    }

    if ("parse-type" in fieldProperties) {
        formInput.dataset.parseType = fieldProperties["parse-type"];
    }

    formInput.dataset.name = key;
    formInput.dataset.type = formInputType;

    if ("avoid-sort" in fieldProperties) {
        formInput.dataset.avoidSort = "";
    }

    if (fieldProperties.readOnly) {
        formInput.dataset.readOnly = fieldProperties.readOnly.toString();
    }

    if (fieldProperties.pattern) {
        formInput.dataset.pattern = fieldProperties.pattern;
    }

    if (fieldProperties.default) {
        formInput.dataset.default = fieldProperties.default;
    }

    // This is a custom property, which we sometimes inject through
    // repository modifications to tell a field that its possible values
    // depends on another formInput.
    if (fieldProperties.dependsOn) {
        formInput.dataset.dependsOn = fieldProperties.dependsOn;
    }

    if (fieldProperties.autocomplete) {
        formInput.dataset.autocomplete = fieldProperties.autocomplete;
    }

    formInput.dataset.label = fieldProperties.title;
    formInput.dataset.placeholder =
        fieldProperties.description || fieldProperties.title;

    if ("required" in fieldProperties && fieldProperties.required) {
        formInput.dataset.required = "";
    }

    return formInput;
}

// Add a prefix to the keys in the object.
function getCopyOfObjectWithPrefixedKeys(
    prefix: string,
    properties: IStekzSchema
): IStekzSchema {
    const copyOfProperties = { ...properties };
    Object.keys(copyOfProperties).forEach((key: string) => {
        copyOfProperties[`${prefix}.${key}`] = copyOfProperties[key];
        delete copyOfProperties[key];
    });
    return copyOfProperties;
}

// Some properties exist of multiple form inputs, e.g. the 'object' type.
// Given the key + property, this function usually returns one form input.
export function createFormInputs(
    action: string,
    properties: IStekzSchema
): IFormInputTypeComponent[] {
    const formInputs = [];
    // Iterate over all the properties for the schema extracted from the
    // openapi spec, and create accompanying form-input elements.
    Object.entries(properties).forEach(
        // When the form action is 'create', don't bother showing the read-only
        // fields, since they are not required for the post.
        ([key, fieldProperties]: [string, IStekzFieldProperties]) => {
            if (
                action === "create" &&
                "readOnly" in fieldProperties &&
                fieldProperties.readOnly
            ) {
                return;
            }

            if (
                fieldProperties.type === "object" &&
                "properties" in fieldProperties
            ) {
                // These 'extra form inputs' are form inputs that belong to a specific
                // key, add a prefix to the keys of the names of the form inputs to
                // indicate what fields these fields belong to.
                const updatedProperties = getCopyOfObjectWithPrefixedKeys(
                    key,
                    fieldProperties.properties
                );
                // Add the parentFieldName e.g. parentFieldName.fieldName
                const objectFormInputs = createFormInputs(
                    action,
                    updatedProperties
                );
                formInputs.push(...objectFormInputs);

                // We don't add form-inputs for an object, only for its nested fields.
                return;
            }

            const formInput = document.createElement("c-form-input");
            enrichFormInput(formInput, key, fieldProperties);
            formInputs.push(formInput);
        }
    );

    return formInputs;
}

// Used by form input choice types, to retrieve the options they can show
// for a specific repository.
export async function getOptionsFromRepository(
    repositoryName: string,
    key: string,
    keyDescription: string,
    parameters: IRepositoryParameters,
    failSilently: boolean,
    maxResultsSize: number = 1000
): Promise<[{ value: string | number; name: string }[]?]> {
    const repository = serviceRepository.get(repositoryName);
    let action = "all";
    // If a given repository only supports one type of action, use that
    // action instead. This is useful to support openapi-generated
    // repositories where the endpoints are not crud-like.
    if (Object.keys(repository.actionPaths).length === 1) {
        action = Object.keys(repository.actionPaths)[0];
    }

    // When this form-input depends on another form-input, chances are that
    // a required parameter is not yet set, because it is indrectly being
    // set by the dependee through form, once a value is chosen on the
    // form-input-choice of the dependee. When the parameter is not yet set,
    // the 'test' function will throw an error when we pass all parameters
    // to it. When an error does occur, we do not throw the error, but
    // instead we silently return an empty options list instead.
    if (failSilently) {
        try {
            repository.objects.test(action, parameters);
        } catch {
            return [];
        }
    }

    const isPaginatedThroughRepository =
        !!repository?.actionPaths?.[action]?.info?.isPaginated;
    if (isPaginatedThroughRepository) {
        parameters["size"] = maxResultsSize;
    }

    const response = await repository.objects[action](parameters);
    const json = await response.json();

    const itemRef = isPaginatedThroughRepository ? json.items : json;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return itemRef.map((item: any) => {
        const value = item[key];
        // Fall back to using value as name if no name is given.
        const name = lodashGet(item, keyDescription) ?? value;

        return { value, name };
    });
}

// Collect the form input name + value. If required, the name + value is
// stored in the passed formData, to be passed to the repository.
function collectFormInputValueIfNecessary(
    formData: IFormData,
    formInputNode: IFormInputTypeComponent
) {
    const value = formInputNode.value;
    // When the value is "" or undefined, and the field is not
    // required, avoid adding it to the formData.
    if (
        typeof value !== "boolean" &&
        (value === "" || value === undefined) &&
        !formInputNode.dataset.required
    ) {
        return;
    }

    formData[formInputNode.dataset.name] = value;
}

// Used by form, to retrieve the formData from
// form inputs and nested form inputs
export async function getFormDataFromElement(element: HTMLElement) {
    // Keep a list of promises, which store all async processes executed by
    // nested form inputs that need to be awaited.
    const promises = [];
    const formData: IFields = {};

    const formInputs = [
        ...element.querySelectorAll("c-form-input:not([data-read-only])"),
        ...element.querySelectorAll("c-table[data-name]"),
    ];

    if ("extraFormInputs" in element) {
        element.extraFormInputs.forEach((extraFormInputTagName) => {
            if (extraFormInputTagName.includes("c-")) {
                formInputs.push(element.querySelector(extraFormInputTagName));
            }
        });
    }

    // Collect all names and values for nested form inputs, and store
    // their keys+values in formData.
    formInputs.forEach((formInputNode) => {
        // If there is some preprocessing required, wait until
        // that is finished before trying to get the value from the
        // form-input.
        if (formInputNode.requiresPreprocessing) {
            const promise = formInputNode.preprocess().then(() => {
                collectFormInputValueIfNecessary(formData, formInputNode);
            });
            promises.push(promise);
        } else {
            collectFormInputValueIfNecessary(formData, formInputNode);
        }
    });

    try {
        await Promise.all(promises);
    } catch (e) {
        // A 'hint' for the developer below.
        // The pre-processing form-input is responsible for showing an
        // error, so the user should know what is going on. E.g. when a
        // c-form-input-presigned-fileupload is not able to retrieve a
        // presigned url during it's 'preprocessing' stage, the c-form-input
        // itself needs to display the error.
        monitoring.error(
            "Something went wrong in preprocessing our form-inputs, not continuing with form submission.",
            e
        );
        return;
    }
    return unflattenFormData(formData);
}

// Turns e.g. {'someField.nestedField': 'a'} into {
//   someField: {
//     nestedField: 'a'
//   }
// }
function unflattenFormData(formData: IFields): IFields {
    const restructuredFormData = {};
    Object.keys(formData).forEach((key) => {
        lodashSet(restructuredFormData, key, formData[key]);
    });
    return restructuredFormData;
}

// Get the properties that belong to the given form input, by extracting that
// information from the DOM context of the given node.
export function getPropsForFormInput(
    formInputElem: HTMLElement
): IStekzFieldProperties {
    const target: HTMLElement = dom.getFirstMatchingParentElement(
        formInputElem,
        ["c-form", "c-table"],
        ["serviceRepository"]
    ) as HTMLElementTagNameMap["c-form"];

    // The form input should be placed in a form.
    if (!target) {
        return;
    }

    // No need to throw an error about this; this will be done by the
    // c-form itself. However, we can't derive any props for the given form-input
    // without it.
    if (!("serviceRepository" in target.dataset)) {
        return;
    }

    const isForm = target.tagName.toLowerCase() === "c-form";
    // Can't do anything if action is not defined on the form, for tables
    // we can always use the 'update' action.
    if (isForm && !("action" in target.dataset)) {
        return;
    }

    // Can't do anything when name is not in the form input.
    if (!("name" in formInputElem.dataset)) {
        return;
    }

    // When getting props for table, we can assume that we are used in an inline
    // context, where the fields are used to update an existing entry.
    const repositoryAction = isForm ? target.dataset.action : "update";

    // Get rid of any numbers that might describe that the form input is
    // describing an element in an array.
    // E.g. shipments.container.0.number could describe
    // {
    //   shipments: {
    //     container: [{
    //       number < this key
    //     }]
    //   }
    // }
    // Since the properties are not defined in an array, so we can simply remove
    // the number to end up with something like 'shipments.container.number'
    // instead.
    const fieldName = formInputElem.dataset.name.replace(/\.[0-9]+?/g, "");
    const repositoryName = target.dataset.serviceRepository;
    const repository = serviceRepository.get(repositoryName);
    return getFieldProperties(
        fieldName,
        repository.properties[repositoryAction]
    );
}

// Traverse up the tree until we find c-forms-dirty-checker.
export function getFormsDirtyCheckerElement(
    context: Node | ShadowRoot
): HTMLElementTagNameMap["c-forms-dirty-checker"] | undefined {
    let formsDirtyCheckerElement:
        | HTMLElementTagNameMap["c-forms-dirty-checker"]
        | undefined;
    let currentNode = context;
    while (currentNode) {
        currentNode = dom.getParentOrHost(currentNode);
        if (dom.isMatchingElement(currentNode, "c-forms-dirty-checker")) {
            formsDirtyCheckerElement = currentNode;
            break;
        }
    }
    return formsDirtyCheckerElement;
}
